import type React from "react";
import {
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Paper,
  useTheme,
  useMediaQuery,
  Drawer,
} from "@mui/material";
import {
  Person as PersonIcon,
  CalendarMonth as CalendarIcon,
  CardMembership as MembershipIcon,
  Settings as SettingsIcon,
  School as ProgramsIcon,
  Share as ShareIcon,
} from "@mui/icons-material";
import type { DashboardSection } from "./Dashboard";

interface DashboardSidebarProps {
  activeSection: DashboardSection;
  onSectionChange: (section: DashboardSection) => void;
  mobileOpen?: boolean;
  onMobileToggle?: () => void;
}

// Define the sidebar navigation items
const sidebarItems = [
  { id: "profile" as DashboardSection, label: "Profile", icon: <PersonIcon /> },
  {
    id: "bookings" as DashboardSection,
    label: "Bookings",
    icon: <CalendarIcon />,
  },
  {
    id: "memberships" as DashboardSection,
    label: "Memberships",
    icon: <MembershipIcon />,
  },
  {
    id: "programs" as DashboardSection,
    label: "Programs",
    icon: <ProgramsIcon />,
  },
  {
    id: "referrals" as DashboardSection,
    label: "Referrals",
    icon: <ShareIcon />,
  },
  // {
  //   id: "payments" as DashboardSection,
  //   label: "Payments",
  //   icon: <PaymentIcon />,
  // },
  {
    id: "settings" as DashboardSection,
    label: "Settings",
    icon: <SettingsIcon />,
  },
];

const DashboardSidebar: React.FC<DashboardSidebarProps> = ({
  activeSection,
  onSectionChange,
  mobileOpen = false,
  onMobileToggle,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const sidebarContent = (
    <List sx={{ py: 1 }}>
      {sidebarItems.map((item) => (
        <ListItem key={item.id} disablePadding>
          <ListItemButton
            selected={activeSection === item.id}
            onClick={() => onSectionChange(item.id)}
            sx={{
              borderRadius: 1,
              mx: 1,
              "&.Mui-selected": {
                backgroundColor: "primary.light",
                color: "#ffffff",
                "&:hover": {
                  backgroundColor: "primary.light",
                },
              },
            }}
          >
            <ListItemIcon
              sx={{
                minWidth: 40,
              }}
            >
              {item.icon}
            </ListItemIcon>
            <ListItemText primary={item.label} />
          </ListItemButton>
        </ListItem>
      ))}
    </List>
  );

  // Mobile drawer
  if (isMobile) {
    return (
      <>
        {/* <IconButton
          color="inherit"
          aria-label="open drawer"
          edge="start"
          onClick={onMobileToggle}
          sx={{ mr: 2, display: { md: "none" } }}
        >
          <MenuIcon />
        </IconButton> */}

        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={onMobileToggle}
          ModalProps={{
            keepMounted: true, // Better open performance on mobile
          }}
          sx={{
            display: { xs: "block", md: "none" },
            "& .MuiDrawer-paper": {
              boxSizing: "border-box",
              width: 240,
              backgroundColor: theme.palette.background.paper,
            },
          }}
        >
          {sidebarContent}
        </Drawer>
      </>
    );
  }

  // Desktop sidebar
  return (
    <Paper
      elevation={1}
      sx={{
        width: 240,
        borderRadius: 2,
        display: { xs: "none", md: "block" },
        height: "fit-content",
        position: "sticky",
        top: 80,
        alignSelf: "flex-start",
        maxHeight: "calc(100vh - 50px)",
        overflowY: "auto",
      }}
    >
      {sidebarContent}
    </Paper>
  );
};

export default DashboardSidebar;
